/**
 * SENRITSUX STRONG PASSWORD VALIDATOR
 * 
 * Provides centralized, consistent password validation, emoji detection,
 * user-info leak prevention, common-password filtering, and strength scoring.
 */

// Comprehensive Unicode Emoji regular expression
// Matches pictographs, emoji presentations, surrogates, variation selectors, ZWJ sequences, skin tone modifiers, and flags
export const EMOJI_REGEX = /(?:\p{Extended_Pictographic}|\p{Emoji_Presentation}|\uFE0F|\u200D|\u20E3|[\u{1F1E6}-\u{1F1FF}]|[\u{1F300}-\u{1FAFF}]|[\u{2600}-\u{27BF}])/u;

/**
 * Common and weak base passwords list (in lowercase).
 * Includes the minimum mandatory list and widespread weak passwords/walks.
 */
export const COMMON_PASSWORDS = new Set([
  '000000',
  '0000000',
  '00000000',
  '111111',
  '1111111',
  '11111111',
  '111111111',
  '123456',
  '1234567',
  '12345678',
  '123456789',
  '1234567890',
  'password',
  'pass1234',
  'qwerty',
  'qwerty123',
  'qwertyuiop',
  'abc123',
  'admin',
  'admin123',
  'letmein',
  'welcome',
  'iloveyou',
  'master',
  'sunshine',
  'princess',
  'football',
  'shadow',
  'superman',
  'monkey',
  'dragon',
  'mustang',
  'charlie',
  'donald',
  'default',
  'login123',
  'senritsu',
  'senritsux',
  'secret',
  'trustno1',
  'asdfgh',
  'zxcvbnm',
]);

/** Base common words used to detect simple variations like "qwerty123" or "admin@123" */
const COMMON_ROOT_WORDS = [
  'password',
  'qwerty',
  'admin',
  'letmein',
  'welcome',
  'iloveyou',
  'master',
  'default',
  'senritsu',
  'senritsux',
  'secret',
  'login',
  'pass',
];

/**
 * Checks if a string contains any emoji character.
 * @param {string} str 
 * @returns {boolean}
 */
export function containsEmoji(str) {
  if (!str || typeof str !== 'string') return false;
  try {
    return EMOJI_REGEX.test(str);
  } catch {
    // Fallback for environments lacking full Unicode property escape support
    return /[\uD83C-\uDBFF\uDC00-\uDFFF\u2600-\u27BF]/.test(str);
  }
}

/**
 * Checks if a password contains repeated characters or simple cyclic patterns.
 * Examples: 'aaaaaaa', '11111111', '!!!!!!!!', '........', 'abababab', '12121212'
 * @param {string} password 
 * @returns {boolean}
 */
export function isRepeatedPattern(password) {
  if (!password || typeof password !== 'string') return false;
  
  // 4 or more identical consecutive characters (e.g. 'aaaa', '0000', '!!!!')
  if (/(.)\1{3,}/.test(password)) {
    return true;
  }

  // 2-character repeating pattern repeated 3+ times (e.g. 'ababab', '121212')
  if (/(.{2})\1{2,}/.test(password)) {
    return true;
  }

  // 3-character repeating pattern repeated 2+ times (e.g. 'abcabcabc')
  if (/(.{3})\1{2,}/.test(password)) {
    return true;
  }

  // Low character diversity on 6+ character passwords (e.g. only 1 or 2 unique characters)
  if (password.length >= 6) {
    const uniqueChars = new Set(password).size;
    if (uniqueChars <= 2) {
      return true;
    }
  }

  return false;
}

/**
 * Checks if a password is an obviously common password or a trivial variation of one.
 * @param {string} password 
 * @returns {boolean}
 */
export function isCommonPassword(password) {
  if (!password || typeof password !== 'string') return false;
  const lower = password.toLowerCase();

  // Exact match in common passwords set
  if (COMMON_PASSWORDS.has(lower)) {
    return true;
  }

  // Repeated pattern check
  if (isRepeatedPattern(lower)) {
    return true;
  }

  // Check if password is a common root word + common digits/symbols (e.g. "admin@123", "qwerty123", "password2026")
  for (const root of COMMON_ROOT_WORDS) {
    // If the root word constitutes the core (>60%) of the password or starts with it
    if (lower.startsWith(root) || lower.endsWith(root)) {
      const remainder = lower.replace(root, '');
      // If remainder is only digits, spaces, or simple punctuation (like "123", "@123", "2026")
      if (!remainder || /^[\d!@#$%^&*()_+\-=[\]{}|;:,.<>?/~`'\s]+$/.test(remainder)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Checks if the password contains user info such as email local part, full name, or stage name.
 * Normalizes email and name comparisons carefully to avoid rejecting unrelated passwords
 * due to very short name fragments (e.g. 'Dan' in 'Standard' or 'Lee' in 'Sleep').
 * 
 * @param {string} password 
 * @param {object} userContext - { email, fullName, stageName }
 * @returns {{ contains: boolean, matchedField?: string }}
 */
export function checkUserInfoInPassword(password, userContext = {}) {
  if (!password || typeof password !== 'string') {
    return { contains: false };
  }

  const lowerPass = password.toLowerCase();
  const { email, fullName, stageName } = userContext;

  // 1. Check Email
  if (email && typeof email === 'string') {
    const lowerEmail = email.toLowerCase().trim();
    if (lowerEmail && lowerPass.includes(lowerEmail)) {
      return { contains: true, matchedField: 'email' };
    }

    // Extract email local part before '@'
    const localPart = lowerEmail.split('@')[0];
    if (localPart) {
      if (localPart.length >= 4 && lowerPass.includes(localPart)) {
        return { contains: true, matchedField: 'email' };
      }
      if (localPart.length === 3) {
        // Only reject 3-char local part if bounded by non-letters or at string boundaries
        const regex3 = new RegExp(`(?:^|[^a-z0-9])${localPart}(?:[^a-z0-9]|$)`, 'i');
        if (regex3.test(lowerPass)) {
          return { contains: true, matchedField: 'email' };
        }
      }

      // Check sub-tokens of local part
      const tokens = localPart.split(/[._+-]/).filter((t) => t.length >= 4);
      for (const token of tokens) {
        if (lowerPass.includes(token)) {
          return { contains: true, matchedField: 'email' };
        }
      }
    }
  }

  // Helper for names
  const checkNameTokens = (nameVal, fieldName) => {
    if (!nameVal || typeof nameVal !== 'string') return null;
    const cleanName = nameVal.toLowerCase().trim();
    if (!cleanName) return null;

    // Check whole normalized name without spaces/punctuation (e.g. 'myartist' or 'rahulsharma')
    const condensed = cleanName.replace(/[\s._-]/g, '');
    if (condensed.length >= 4 && lowerPass.includes(condensed)) {
      return fieldName;
    }

    // Check individual name parts (tokens >= 4 chars, e.g. 'Rahul' from 'Rahul Sharma')
    const tokens = cleanName.split(/[\s._-]+/).filter((t) => t.length >= 4);
    for (const token of tokens) {
      if (lowerPass.includes(token)) {
        return fieldName;
      }
    }

    // For 3-letter names (e.g. 'Dan' or 'Roy' or 'Lee'), only match if isolated by non-letters
    // to avoid false positives on words like 'standard', 'destroy', 'sleep', 'guidance'
    const shortTokens = cleanName.split(/[\s._-]+/).filter((t) => t.length === 3);
    for (const shortToken of shortTokens) {
      const regex3 = new RegExp(`(?:^|[^a-z0-9])${shortToken}(?:[^a-z0-9]|$)`, 'i');
      if (regex3.test(lowerPass)) {
        return fieldName;
      }
    }

    return null;
  };

  // 2. Check Full Name
  const matchedFullName = checkNameTokens(fullName, 'fullName');
  if (matchedFullName) {
    return { contains: true, matchedField: 'fullName' };
  }

  // 3. Check Stage Name
  const matchedStageName = checkNameTokens(stageName, 'stageName');
  if (matchedStageName) {
    return { contains: true, matchedField: 'stageName' };
  }

  return { contains: false };
}

/**
 * Evaluates individual progressive requirement criteria for live UI feedback.
 * @param {string} password 
 * @param {object} userContext - { email, fullName, stageName }
 * @returns {object}
 */
export function getPasswordRequirements(password = '', userContext = {}) {
  const pwd = typeof password === 'string' ? password : '';
  const hasEmoji = containsEmoji(pwd);
  const userInfoCheck = checkUserInfoInPassword(pwd, userContext);
  const isCommon = isCommonPassword(pwd);

  return {
    minLength: pwd.length >= 8,
    hasUppercase: /[A-Z]/.test(pwd),
    hasLowercase: /[a-z]/.test(pwd),
    hasNumber: /[0-9]/.test(pwd),
    hasSpecialChar: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?/~`'\\"]/.test(pwd) || /[^A-Za-z0-9\s]/.test(pwd),
    hasNoEmoji: !hasEmoji,
    hasNoUserInfo: !userInfoCheck.contains,
    isNotCommon: !isCommon,
    // Raw status flags
    hasEmoji,
    userInfoCheck,
    isCommon,
  };
}

/**
 * Calculates password strength score (0-4) and level ('Weak', 'Fair', 'Good', 'Strong').
 * @param {string} password 
 * @param {object} requirements 
 * @returns {{ score: number, level: string }}
 */
export function calculateStrength(password = '', requirements) {
  if (!password) {
    return { score: 0, level: 'Empty' };
  }

  const {
    minLength,
    hasUppercase,
    hasLowercase,
    hasNumber,
    hasSpecialChar,
    hasNoEmoji,
    hasNoUserInfo,
    isNotCommon,
  } = requirements;

  // If any mandatory security rule fails, it is Weak
  if (!minLength || !hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar || !hasNoEmoji || !hasNoUserInfo || !isNotCommon) {
    return { score: 1, level: 'Weak' };
  }

  // Base score for meeting all core requirements
  let score = 2; // Fair

  // Length bonuses
  if (password.length >= 12) {
    score = 4; // Strong
  } else if (password.length >= 10) {
    score = 3; // Good
  }

  // Character diversity check
  const uniqueCount = new Set(password).size;
  if (score === 3 && uniqueCount >= 8 && password.length >= 11) {
    score = 4;
  }

  const levelMap = {
    1: 'Weak',
    2: 'Fair',
    3: 'Good',
    4: 'Strong',
  };

  return {
    score,
    level: levelMap[score] || 'Fair',
  };
}

/**
 * Comprehensive password validation function.
 * Validates a password against all SenritsuX security policies.
 * 
 * @param {string} password 
 * @param {object} userContext - { email, fullName, stageName }
 * @returns {{
 *   isValid: boolean,
 *   score: number,
 *   level: string,
 *   error: string | null,
 *   errors: string[],
 *   requirements: object
 * }}
 */
export function validatePassword(password = '', userContext = {}) {
  const pwd = typeof password === 'string' ? password : '';
  const errors = [];

  if (!pwd) {
    return {
      isValid: false,
      score: 0,
      level: 'Empty',
      error: 'Password is required.',
      errors: ['Password is required.'],
      requirements: getPasswordRequirements('', userContext),
    };
  }

  const reqs = getPasswordRequirements(pwd, userContext);

  // 1. Emoji check
  if (reqs.hasEmoji) {
    errors.push('Emojis are not allowed in passwords.');
  }

  // 2. Minimum length
  if (!reqs.minLength) {
    errors.push('Password must be at least 8 characters.');
  }

  // 3. Uppercase requirement
  if (!reqs.hasUppercase) {
    errors.push('Password must contain at least one uppercase letter.');
  }

  // 4. Lowercase requirement
  if (!reqs.hasLowercase) {
    errors.push('Password must contain at least one lowercase letter.');
  }

  // 5. Number requirement
  if (!reqs.hasNumber) {
    errors.push('Password must contain at least one number.');
  }

  // 6. Special character requirement
  if (!reqs.hasSpecialChar) {
    errors.push('Password must contain at least one special character.');
  }

  // 7. User info inclusion
  if (!reqs.hasNoUserInfo) {
    if (reqs.userInfoCheck.matchedField === 'email') {
      errors.push('Password must not contain your email address.');
    } else {
      errors.push('Password must not contain your name or stage name.');
    }
  }

  // 8. Common / repeated password
  if (!reqs.isNotCommon) {
    errors.push('This password is too common or easily guessed. Please choose a stronger password.');
  }

  const { score, level } = calculateStrength(pwd, reqs);
  const isValid = errors.length === 0;

  return {
    isValid,
    score: isValid ? score : 1,
    level: isValid ? level : 'Weak',
    error: errors.length > 0 ? errors[0] : null,
    errors,
    requirements: reqs,
  };
}
